import { useEffect, useRef, useState } from "react";
import { cx } from "./ui";

/** Draw with a finger, a pen, or a mouse. */
export function SignaturePad({ canvas, onDrawn, label = "Your signature" }: { canvas: React.RefObject<HTMLCanvasElement | null>; onDrawn: (v: boolean) => void; label?: string }) {
  const drawing = useRef(false);
  const [empty, setEmpty] = useState(true);
  /** Sizes the drawing surface to the box (it may be hidden at first, e.g. in a dialog that's opening). */
  const fit = (c: HTMLCanvasElement) => {
    const ratio = window.devicePixelRatio || 1;
    if (c.width === Math.round(c.offsetWidth * ratio) && c.height === Math.round(c.offsetHeight * ratio)) return;
    c.width = Math.round(c.offsetWidth * ratio);
    c.height = Math.round(c.offsetHeight * ratio);
    const ctx = c.getContext("2d")!;
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#111";
  };
  useEffect(() => {
    fit(canvas.current!);
  }, [canvas]); // eslint-disable-line react-hooks/exhaustive-deps
  const point = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    return [e.clientX - r.left, e.clientY - r.top] as const;
  };
  const clear = () => {
    const c = canvas.current!;
    c.getContext("2d")!.clearRect(0, 0, c.width, c.height);
    setEmpty(true);
    onDrawn(false);
  };
  return (
    <div>
      <div className="mb-1 flex items-end justify-between">
        <span className="text-sm font-semibold text-ink-soft">{label}</span>
        {!empty && (
          <button className="text-sm text-muted hover:text-ink" onClick={clear}>
            Clear
          </button>
        )}
      </div>
      <canvas
        ref={canvas}
        aria-label="Signature pad: draw your signature"
        className={cx("h-40 w-full touch-none rounded-[var(--radius-control)] border border-dashed bg-white", empty ? "border-line" : "border-ink-soft")}
        onPointerDown={(e) => {
          if (empty) fit(e.currentTarget);
          drawing.current = true;
          e.currentTarget.setPointerCapture(e.pointerId);
          const ctx = e.currentTarget.getContext("2d")!;
          const [x, y] = point(e);
          ctx.beginPath();
          ctx.moveTo(x, y);
        }}
        onPointerMove={(e) => {
          if (!drawing.current) return;
          const ctx = e.currentTarget.getContext("2d")!;
          const [x, y] = point(e);
          ctx.lineTo(x, y);
          ctx.stroke();
          if (empty) {
            setEmpty(false);
            onDrawn(true);
          }
        }}
        onPointerUp={() => (drawing.current = false)}
        onPointerLeave={() => (drawing.current = false)}
      />
      {empty && <p className="mt-1 text-xs text-muted">Sign in the box above.</p>}
    </div>
  );
}
